import { CheckIcon } from "@heroicons/react/20/solid";
import * as Dialog from "@radix-ui/react-dialog";
import clsx from "clsx";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import { useState } from "react";
import {
  EnumSubscriptionInterval,
  EnumUserSubscriptionType,
} from "../../__generated__/graphql";
import { useToastsContext } from "../../contexts/toasts";
import useStores from "../../hooks/useStores";
import ButtonLarge from "../ButtonLarge";

type PricingCardProps = {
  id: string;
  title: string;
  isAnnual: boolean;
  subscriptionType: string;
  features: Array<{ text: string; included: boolean }>;
  price?: { monthly: number; annually: number };
  isHighlighted?: boolean;
  isFree?: boolean;
  onSelectPricingPlan: (
    subscriptionType: string,
    paymentInterval: string
  ) => void;
  isActive?: boolean | ((isAnnual: boolean) => boolean);
  isDisabled?: boolean;
  isUserSubscribed?: boolean;
  isEnterprise?: boolean;
};

const PricingCard = ({
  title,
  price,
  features,
  isHighlighted,
  isFree,
  isAnnual,
  subscriptionType,
  isActive,
  isDisabled,
  onSelectPricingPlan,
  isUserSubscribed,
  isEnterprise,
}: PricingCardProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const isActiveState =
    typeof isActive === "boolean" ? isActive : isActive?.(isAnnual);

  const handlePlanSelection = async () => {
    if (isActiveState) return;
    setIsLoading(true);
    try {
      await onSelectPricingPlan(
        subscriptionType,
        isAnnual ? "Annually" : "Monthly"
      );
    } finally {
      setIsLoading(false);
      setShowConfirmDialog(false);
    }
  };

  const renderPricing = () => {
    if (isFree) {
      return <span className="text-4xl font-bold text-gray-900">FREE</span>;
    }

    if (isEnterprise) {
      return <span className="text-4xl font-bold text-gray-900">Custom</span>;
    }

    if (price) {
      return (
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-gray-900">
              ${isAnnual ? price.annually : price.monthly}
            </span>
            <span className="text-gray-400">
              /{isAnnual ? "year" : "month"}
            </span>
          </div>
          {isAnnual && (
            <p className="mt-1.5 text-sm text-gray-400">
              ${(price.annually / 12).toFixed(2)} per month
            </p>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className={clsx(
        "relative flex h-full flex-col rounded-2xl bg-white p-6 transition-all",
        isHighlighted
          ? "border-2 border-blue-500 shadow-xl shadow-blue-500/10"
          : "border border-gray-200 hover:border-gray-300 hover:shadow-md"
      )}
    >
      {isHighlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 px-3 py-1 text-xs font-semibold text-white shadow-sm">
          Most Popular
        </div>
      )}

      <h3 className="text-xl font-bold text-gray-900">{title}</h3>

      <div className="mt-4 min-h-[72px]">{renderPricing()}</div>

      <ul className="mt-6 flex-grow space-y-3.5">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-50">
              <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
            </span>
            <span className="text-sm leading-relaxed text-gray-600">
              {feature.text}
            </span>
          </li>
        ))}
      </ul>

      {!isFree && (
        <div className="mt-8">
          {isActiveState ? (
            <div className="rounded-full border border-gray-200 bg-gray-50 py-3 text-center text-sm font-semibold text-gray-500">
              Current Plan
            </div>
          ) : isEnterprise ? (
            <button
              type="button"
              onClick={() =>
                window.open(
                  "https://calendly.com/paul-gradingpal/30min",
                  "_blank"
                )
              }
              className="w-full rounded-full border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-800 transition-all hover:bg-gray-50"
            >
              Book a Demo
            </button>
          ) : (
            <Dialog.Root
              open={showConfirmDialog}
              onOpenChange={setShowConfirmDialog}
            >
              <Dialog.Trigger asChild>
                <button
                  type="button"
                  disabled={isDisabled || isActiveState || isLoading}
                  onClick={() => {
                    if (isUserSubscribed && !isActiveState) {
                      setShowConfirmDialog(true);
                    } else {
                      handlePlanSelection();
                    }
                  }}
                  className={clsx(
                    "w-full rounded-full py-3 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50",
                    isHighlighted
                      ? "bg-gradient-to-r from-blue-600 to-violet-600 text-white shadow-md shadow-blue-500/25 hover:shadow-lg"
                      : "border border-gray-800 bg-gray-900 text-white hover:bg-gray-800"
                  )}
                >
                  {isLoading
                    ? "Processing..."
                    : isDisabled
                    ? "Not Available"
                    : isUserSubscribed && !isActiveState
                    ? "Upgrade"
                    : "Choose plan"}
                </button>
              </Dialog.Trigger>

              <Dialog.Portal>
                <Dialog.Overlay className="fixed inset-0 z-[10001] bg-black/50" />
                <Dialog.Content className="fixed left-1/2 top-1/2 z-[10002] w-full max-w-md -translate-x-1/2 -translate-y-1/2 transform rounded-2xl bg-white p-6 shadow-2xl">
                  <h3 className="mb-4 text-lg font-bold text-gray-900">
                    Confirm Plan Upgrade
                  </h3>
                  <p className="mb-6 text-sm leading-relaxed text-gray-600">
                    Are you sure you want to upgrade to the {title} -{" "}
                    {isAnnual ? "Yearly" : "Monthly"} plan?
                    <br />
                    <br />
                    Note: You will be charged a{" "}
                    <a
                      href="https://docs.stripe.com/billing/subscriptions/prorations"
                      target="_blank"
                      className="text-blue-600 underline"
                    >
                      prorated amount
                    </a>{" "}
                    based upon the remaining duration of your current plan.
                  </p>
                  <div className="flex justify-end gap-3">
                    <ButtonLarge
                      type="button"
                      theme="secondary"
                      onClick={() => setShowConfirmDialog(false)}
                      buttonText="Cancel"
                    />
                    <ButtonLarge
                      type="button"
                      theme="primary"
                      onClick={handlePlanSelection}
                      buttonText="Confirm Upgrade"
                    />
                  </div>
                </Dialog.Content>
              </Dialog.Portal>
            </Dialog.Root>
          )}
        </div>
      )}
    </div>
  );
};

// Annual savings vs paying monthly, derived from the live Copilot prices so
// the badge never drifts from the actual numbers when pricing changes.
const monthlyPrice = Number(import.meta.env.VITE_APP_PRO_PLAN_MONTHLY_PRICE);
const annualPrice = Number(import.meta.env.VITE_APP_PRO_PLAN_ANNUAL_PRICE);
const annualSavingsPct =
  monthlyPrice > 0 && annualPrice > 0
    ? Math.round((1 - annualPrice / (monthlyPrice * 12)) * 100)
    : 0;

const PricingComponent = () => {
  const { auth, payments } = useStores();
  const { addToast } = useToastsContext();
  const [frequency, setFrequency] = useState("annual");

  // If user is pro user and not enterprise user, we need to showcase the billing portal button
  const isProWithoutFreeTrialAndEnterprise =
    auth.user?.isProWithoutFreeTrialAndEnterprise;

  const user = toJS(auth.user?.toGQLAttributes());

  const plans = [
    {
      id: "alayna-copilot-free",
      title: "AskAlayna Free Plan",
      isFree: true,
      features: [
        { text: "10 Chatbot uses", included: true },
        { text: "3 Magic Materials uses", included: true },
        { text: "3 Image Creations uses", included: true },
      ],
      subscriptionType: "Free",
    },
    {
      id: "alayna-copilot-pro",
      title: "AskAlayna Pro Plan",
      price: {
        monthly: import.meta.env.VITE_APP_PRO_PLAN_MONTHLY_PRICE,
        annually: import.meta.env.VITE_APP_PRO_PLAN_ANNUAL_PRICE,
      },
      features: [
        { text: "Unlimited Chatbot uses", included: true },
        { text: "Unlimited Magic Materials uses", included: true },
        { text: "Unlimited Image Creations uses", included: true },
      ],
      subscriptionType: "Copilot",
      isActive: (isAnnual: boolean) =>
        user?.subscriptionType === "Copilot" &&
        ((isAnnual && user.subscriptionInterval === "Annually") ||
          (!isAnnual && user.subscriptionInterval === "Monthly")),
      isDisabled: user?.subscriptionType === "CopilotSlidesBundle",
    },
    {
      id: "alayna-copilot-slides-pro-bundle",
      title: "AskAlayna + Slides Bundle",
      price: {
        monthly: import.meta.env.VITE_APP_PRO_PLAN_BUNDLE_MONTHLY_PRICE,
        annually: import.meta.env.VITE_APP_PRO_PLAN_BUNDLE_ANNUAL_PRICE,
      },
      isHighlighted: true,
      features: [
        { text: "All AskAlayna Pro features", included: true },
        { text: "All Slides Generator Pro features", included: true },
        { text: "Save additional $49 with this bundle", included: true },
      ],
      subscriptionType: "CopilotSlidesBundle",
      isActive: (isAnnual: boolean) =>
        user?.subscriptionType === "CopilotSlidesBundle" &&
        ((isAnnual && user.subscriptionInterval === "Annually") ||
          (!isAnnual && user.subscriptionInterval === "Monthly")),
    },
    {
      id: "enterprise",
      title: "Schools & Districts Plan",
      isEnterprise: true,
      features: [
        { text: "Volume discounts", included: true },
        { text: "Free Professional Development", included: true },
        { text: "Dedicated support", included: true },
        { text: "Single Sign-On & LMS Integration", included: true },
      ],
      subscriptionType: "Enterprise",
    },
  ];

  const handlePlanSelection = async (
    subscriptionType: string,
    paymentInterval: string
  ) => {
    try {
      if (user?.subscriptionType) {
        // Upgrade subscription
        const { data, error } = await payments.upgradeSubscription(
          subscriptionType as EnumUserSubscriptionType,
          paymentInterval as EnumSubscriptionInterval
        );

        if (data) {
          addToast("Subscription updated successfully", { type: "success" });
          auth.refreshSubscriptionUser();
        } else if (error) {
          addToast(error, { type: "error" });
        }
      } else {
        // Create new subscription
        const { data, error } = await payments.createStripeCheckoutSession(
          subscriptionType as EnumUserSubscriptionType,
          paymentInterval as EnumSubscriptionInterval
        );

        if (error) {
          addToast(error, { type: "error" });
        } else {
          window.location.href = data;
        }
      }
    } catch (error) {
      addToast("An error occurred. Please try again.", { type: "error" });
    }
  };

  const handleManageSubscription = async () => {
    try {
      const url = await auth.redirectToStripeCustomerPortal();
      if (url) {
        window.location.href = url;
      } else {
        addToast("Failed to redirect to billing portal. Please try again.", {
          type: "error",
        });
      }
    } catch (error) {
      addToast("An error occurred. Please try again.", { type: "error" });
    }
  };

  if (!user) return null;

  return (
    <div className="py-10">
      {/* Header */}
      <div className="mx-auto mb-8 max-w-2xl px-4 text-center">
        <h2 className="text-3xl font-bold text-gray-900">
          Choose the plan that fits you
        </h2>
        <p className="mt-2 text-gray-500">
          Unlock unlimited access to every Alayna feature.
        </p>
      </div>

      {/* Special Offer Banner */}
      {!isProWithoutFreeTrialAndEnterprise && (
        <div className="mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-pink-100 bg-gradient-to-r from-pink-50 to-rose-50 p-4 text-center">
            <p className="font-medium text-pink-700">
              🎓 Special Offer: Get FREE access to our{" "}
              <a
                href="https://aicourse.podia.com/using-ai-as-a-teaching-assistant"
                target="_blank"
                className="underline hover:text-pink-800"
              >
                "AI for Educators 101"
              </a>{" "}
              course when you purchase any PRO plan yearly!
            </p>
          </div>
        </div>
      )}

      {isProWithoutFreeTrialAndEnterprise && (
        <div className="mx-auto mb-8 max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-between gap-4 rounded-2xl border border-blue-100 bg-blue-50 p-4 text-center sm:flex-row sm:text-left">
            <p className="font-medium text-blue-700">
              You are already a PRO user. Manage your subscription and view your
              invoices here.
            </p>
            <ButtonLarge
              type="button"
              theme="primary"
              onClick={handleManageSubscription}
              buttonText="View Billing Portal"
            />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Billing Cycle Toggle */}
        <div className="mb-12 flex justify-center">
          <div className="inline-flex items-center rounded-full bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setFrequency("monthly")}
              className={clsx(
                "rounded-full px-5 py-2 text-sm font-semibold transition-all",
                frequency === "monthly"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setFrequency("annual")}
              className={clsx(
                "flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-semibold transition-all",
                frequency === "annual"
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              )}
            >
              Annual
              {annualSavingsPct > 0 && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  Save {annualSavingsPct}%
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plans.map((plan) => (
            <PricingCard
              key={plan.id}
              {...plan}
              isAnnual={frequency === "annual"}
              isUserSubscribed={!!user.subscriptionType}
              onSelectPricingPlan={handlePlanSelection}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default observer(PricingComponent);
